import React, { useEffect, useState } from 'react';
import { Settings2, Play, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../../utils/api';

const FIELDS = [
  { key: 'default_platform_fee_pct', label: 'Platform Fee (%)', hint: 'Default shift billing fee (per-employer overrides win)' },
  { key: 'marketplace_commission_pct', label: 'Marketplace Commission (%)', hint: 'Platform cut on marketplace sales' },
  { key: 'instant_pay_fee_pct', label: 'Instant Pay Fee (%)', hint: 'Fee on early wage cashouts' },
  { key: 'escrow_worker_pct', label: 'Escrow Worker Fee (%)', hint: 'Worker-side fee on escrow releases' },
  { key: 'escrow_platform_fixed', label: 'Escrow Fixed Fee ($)', hint: 'Flat platform fee per escrow release' },
  { key: 'seat_price_monthly', label: 'Seat Price ($/worker/mo)', hint: 'Worker-seat billing unit price' },
];

/** Super-Admin Revenue Rules — one place to configure every platform fee. */
export default function RevenueRulesPanel() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    apiCall('/api/admin/finance/revenue-rules')
      .then((d) => setRules(d.rules))
      .catch(() => {});
  }, []);

  if (!rules) return null;

  const save = async () => {
    setSaving(true);
    try {
      const d = await apiCall('/api/admin/finance/revenue-rules', {
        method: 'PUT',
        body: JSON.stringify({
          ...rules,
          commission_tiers: (rules.commission_tiers || []).map((t) => ({
            min_monthly_shifts: Number(t.min_monthly_shifts) || 0,
            rate_pct: Number(t.rate_pct) || 0,
          })),
        }),
      });
      setRules(d.rules);
      toast.success('Revenue rules saved');
    } catch (e) {
      toast.error(e?.message || 'Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    try {
      const d = await apiCall('/api/admin/finance/seat-billing/preview');
      setPreview(d);
    } catch (e) {
      toast.error('Failed to load seat billing preview');
    }
  };

  const runSeatBilling = async () => {
    setRunning(true);
    try {
      const d = await apiCall('/api/admin/finance/seat-billing/run', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      toast.success(`Seat billing run: ${d.created} invoices ($${d.total_billed.toFixed(2)}), ${d.skipped_existing} already billed`);
      loadPreview();
    } catch (e) {
      toast.error(e?.message || 'Seat billing run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="revenue-rules-panel">
      <div className="flex items-center gap-2 mb-4">
        <Settings2 className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-gray-900">Revenue Rules</h3>
        <span className="text-xs text-gray-400 ml-auto">Financial OS · Super Admin</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {FIELDS.map(({ key, label, hint }) => (
          <div key={key}>
            <label className="text-xs font-medium text-gray-600 block mb-1" title={hint}>{label}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={rules[key] ?? ''}
              onChange={(e) => setRules({ ...rules, [key]: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              data-testid={`revenue-rule-${key}`}
            />
          </div>
        ))}
      </div>

      {/* Phase 3: tiered agency commissions */}
      <div className="mt-4 pt-4 border-t border-gray-100" data-testid="commission-tiers-section">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="text-sm font-semibold text-gray-800">Tiered Agency Commissions</h4>
            <p className="text-xs text-gray-400">Volume bonus — agents hitting the monthly claimed-shift count get the tier rate (never lowers a negotiated rate)</p>
          </div>
          <button
            onClick={() => setRules({ ...rules, commission_tiers: [...(rules.commission_tiers || []), { min_monthly_shifts: 0, rate_pct: 10 }] })}
            className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"
            data-testid="add-commission-tier-btn"
          >
            + Add Tier
          </button>
        </div>
        {(rules.commission_tiers || []).length === 0 ? (
          <p className="text-xs text-gray-400">No tiers — agents keep their base rates.</p>
        ) : (
          <div className="space-y-2">
            {(rules.commission_tiers || []).map((t, i) => (
              <div key={i} className="flex items-center gap-3 text-sm" data-testid={`commission-tier-row-${i}`}>
                <span className="text-xs text-gray-500">≥</span>
                <input
                  type="number" min="0"
                  value={t.min_monthly_shifts}
                  onChange={(e) => setRules({ ...rules, commission_tiers: rules.commission_tiers.map((x, j) => (j === i ? { ...x, min_monthly_shifts: e.target.value } : x)) })}
                  className="w-24 px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                  data-testid={`tier-min-shifts-${i}`}
                />
                <span className="text-xs text-gray-500">shifts/mo →</span>
                <input
                  type="number" min="0" max="50" step="0.5"
                  value={t.rate_pct}
                  onChange={(e) => setRules({ ...rules, commission_tiers: rules.commission_tiers.map((x, j) => (j === i ? { ...x, rate_pct: e.target.value } : x)) })}
                  className="w-24 px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                  data-testid={`tier-rate-${i}`}
                />
                <span className="text-xs text-gray-500">% commission</span>
                <button
                  onClick={() => setRules({ ...rules, commission_tiers: rules.commission_tiers.filter((_, j) => j !== i) })}
                  className="ml-auto text-xs text-red-400 hover:text-red-600"
                  data-testid={`remove-tier-${i}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-100">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!rules.seat_billing_enabled}
            onChange={(e) => setRules({ ...rules, seat_billing_enabled: e.target.checked })}
            className="rounded border-gray-300"
            data-testid="revenue-rule-seat-enabled"
          />
          Enable worker-seat billing
        </label>
        <div className="ml-auto flex gap-2">
          <button
            onClick={loadPreview}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
            data-testid="seat-billing-preview-btn"
          >
            <Eye className="w-4 h-4" /> Preview Seats
          </button>
          <button
            onClick={runSeatBilling}
            disabled={running || !rules.seat_billing_enabled}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40"
            data-testid="seat-billing-run-btn"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run Seat Billing
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40"
            data-testid="revenue-rules-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Rules
          </button>
        </div>
      </div>

      {preview && (
        <div className="mt-4 border border-gray-100 rounded-xl overflow-hidden" data-testid="seat-billing-preview">
          <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 flex justify-between">
            <span>Seat billing preview — {preview.rows.length} organizations</span>
            <span>Total ${Number(preview.total || 0).toFixed(2)}/mo</span>
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
            {preview.rows.length === 0 && (
              <p className="px-4 py-3 text-sm text-gray-400">No organizations with active assigned workers.</p>
            )}
            {preview.rows.map((r) => (
              <div key={r.organization_id} className="px-4 py-2 flex justify-between text-sm">
                <span className="text-gray-700 truncate">{r.organization_name} <span className="text-gray-400">({r.organization_role})</span></span>
                <span className="text-gray-500">{r.worker_count} seats · <span className="font-semibold text-gray-900">${r.amount.toFixed(2)}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
